import { Session } from '../types/session';
import { formatConferenceTime, formatConferenceDate } from '../lib/timezone';

interface SessionDetailProps {
  session: Session;
  onClose: () => void;
}

export default function SessionDetail({ session, onClose }: SessionDetailProps) {
  const formatTime = (dateString: string) => formatConferenceTime(dateString);

  const formatDate = (dateString: string) => formatConferenceDate(dateString);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-start">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-usc-garnet mb-1">
              {session.title}
            </h2>
            <p className="text-sm text-usc-gray-600">
              {formatDate(session.startTime)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="ml-4 text-usc-gray-500 hover:text-usc-gray-700 transition-colors"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-6 space-y-6">
          {/* Time and Location */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-usc-garnet flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <div className="font-semibold text-usc-gray-900">Time</div>
                <div className="text-usc-gray-700">
                  {formatTime(session.startTime)} - {formatTime(session.endTime)}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-usc-garnet flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <div>
                <div className="font-semibold text-usc-gray-900">Location</div>
                <div className="text-usc-gray-700">
                  {session.room.name}, {session.room.building}
                </div>
              </div>
            </div>
          </div>

          {/* Speaker Information */}
          <div>
            <div className="flex items-start gap-3 mb-2">
              <svg className="w-6 h-6 text-usc-garnet flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <div className="flex-1">
                <div className="font-semibold text-usc-gray-900">Speaker</div>
                <div className="text-lg text-usc-gray-800">{session.speakerName}</div>
                {session.speakerAffiliation && (
                  <div className="text-sm text-usc-gray-600">{session.speakerAffiliation}</div>
                )}
              </div>
            </div>
            {session.speakerBio && (
              <div className="ml-9 mt-2 text-usc-gray-700 whitespace-pre-line">
                {session.speakerBio}
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <h3 className="font-semibold text-usc-gray-900 mb-2">Description</h3>
            <p className="text-usc-gray-700 whitespace-pre-wrap">{session.description}</p>
          </div>

          {/* Prerequisites */}
          {session.prerequisites && (
            <div>
              <h3 className="font-semibold text-usc-gray-900 mb-2">Prerequisites</h3>
              <p className="text-usc-gray-700">{session.prerequisites}</p>
            </div>
          )}

          {/* Materials */}
          {session.materials && session.materials.length > 0 && (
            <div>
              <h3 className="font-semibold text-usc-gray-900 mb-2">Session Materials</h3>
              <ul className="space-y-2">
                {session.materials.map((material, index) => (
                  <li key={index}>
                    <a
                      href={material}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-usc-garnet hover:underline flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      {material}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Track */}
          {session.track && (
            <div>
              <span className="inline-block px-3 py-1 text-sm font-medium bg-usc-gray-100 text-usc-gray-700 rounded-full">
                {session.track}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
